import { useEffect, useRef, useState } from 'react';
import type { ServiceLandingData } from '../../../mocks/service-landings';

interface SLBenefitsProps {
  data: ServiceLandingData;
  onCtaClick: () => void;
}

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function SLBenefits({ data, onCtaClick }: SLBenefitsProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          {/* Header */}
          <div
            className={`max-w-2xl mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-sparkling-2-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Čo získate</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              {data.benefits.heading}
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed">{data.benefits.subheading}</p>
          </div>

          {/* Benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
            {data.benefits.items.map((item, i) => (
              <div
                key={item.title}
                className={`group flex gap-5 bg-zinc-50 hover:bg-white border border-zinc-100 hover:border-zinc-200 rounded-2xl p-6 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100 + 100}ms` }}
              >
                <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-white border border-zinc-100 group-hover:border-orange-200 group-hover:bg-orange-50 transition-all duration-300">
                  <i className={`${item.icon} text-xl text-zinc-400 group-hover:text-orange-500 transition-colors duration-300`} />
                </div>
                <div>
                  <h3 className="text-zinc-900 font-bold text-base mb-2 leading-tight">{item.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div
            className={`bg-zinc-900 rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div>
              <p className="text-white font-bold text-lg mb-1">Zaujalo vás to?</p>
              <p className="text-zinc-400 text-sm">Konzultácia je zadarmo a nezáväzná - len si porozprávame.</p>
            </div>
            <button
              onClick={onCtaClick}
              className="flex-shrink-0 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
            >
              {data.hero.ctaPrimary}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
